import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import { DatepickerInput, SelectInput, TextArea, TextInput, FormBeforeAfterContent, RadioButtons, yesNoOptions } from "../components/FormComponents";
import {
	Button,
	ContentContainer,
	TitleHeader,
	NarrowContentWrapper,
	AlertMessage,
	FieldsLoading
} from "../components/UiComponents";
import { CourseHeader, CourseWrapper } from "../components/CourseComponents";
import { format } from "date-fns";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";

const questions = {
	site_of_incident: '',
	name_of_person_in_charge: '',
	date_of_incident: Date.now(),
	employer_name: '',
	nature_of_incident: '',
	incident_details: '',
	details_of_action_taken: '',
	what_happened_to_injured_person: '',
	employer_contacted: '',
	police_contacted: '',
	ambulance_contacted: '',
	witness_student: '',
	witness_tutor_id: null,
	other_witness_details: '',
}

const IncidentReportPage = () => {
	const {
		state,
		api,
		dispatch
	} = useAppContext();
	useDocumentTitle("Incident Report Form");
	const { incidentId } = useParams();
	const [validationErrors, setValidationErrors] = useState(null);
	const [formData, setFormData] = useState(questions);
	const [error, setError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	// const student = api.findStudentById(studentId);
	const [loading, setLoading] = useState(false);

	let course = api.getCurrentCourse();
	let date = api.getCurrentDate();
	const extraContent = state?.forms_extra_content["incident_report"] || {};
	const isReadOnly = useUserIsReadOnly();

	useEffect(() => {
		if (incidentId) {
			loadExistingIncident();
		}
	}, [incidentId]);

	// eslint-disable-next-line no-unused-vars
	const students = date.attendance;

	const tutors = course.tutors;
	let tutorOptions = tutors ? tutors.map((tutor) => {
		return {
			value: tutor.tutor_id,
			label: `${tutor.first_name} ${tutor.last_name}`,
		};
	}) : []

	async function loadExistingIncident() {
		setLoading(true);
		const { data } = await api.fetchData(
			`/courses/${course.id}/incidents`
		);

		if (data?.incidents) {
			dispatch({
				type: "SET_COURSE_INCIDENTS",
				payload: {
					incidents: data.incidents,
					course_id: course.id,
				},
			});
			// Find this incident
			// eslint-disable-next-line eqeqeq
			let incident = data.incidents.find(incident => incident.id == incidentId);
			if (incident && incident?.answers) {
				console.log(incident)
				setFormData({
					...incident.answers,
					date_of_incident: incident.date_of_incident ? new Date(incident.date_of_incident) : Date.now(),
					witness_tutor_id: incident.witness_tutor_id,
				});
			}
			setLoading(false);
		} else {
			setLoading(false);
		}
	}

	function handleFieldChanged(e) {
		console.log(e)
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	}

	function handleRadioChanged(name, value) {
		setFormData({
			...formData,
			[name]: value,
		});
	}

	async function handleSubmit(event) {
		event.preventDefault();
		setValidationErrors(null);
		setSubmitted(false);
		setError(null);
		setSubmitting(true);

		// All fields in formData must be either 1 or 2 except for comments
		let errors = {};

		// Check every field is not empty
		let ignoreVal = ['witness_tutor_id', 'other_witness_details']
		// eslint-disable-next-line no-unused-vars
		for (const [key, value] of Object.entries(questions)) {
			if (!formData[key] && !ignoreVal.includes(key)) {
				errors[key] = "This field is required";
			}
		}

		// if (!formData.witness_student && !formData.witness_tutor_id) {
		// 	errors.witness = "Please select a witness Participant or Tutor";
		// }

		if (Object.keys(errors).length) {
			setValidationErrors(errors);
			setSubmitting(false);
			return;
		}

		// Convert date to YYYY-MM-DD format
		let date = new Date(formData.date_of_incident);
		let formattedDate = format(date, 'yyyy-MM-dd');

		const dataToPost = {
			...formData,
			incidentId: incidentId ? incidentId : null,
			date_of_incident: formattedDate,
			course_id: course.id,
			tutor_id: state.user.id,
		}

		console.log(dataToPost)

		const { data, error } = await api.fetchData(
			`/forms/incident-report`,
			"POST",
			dataToPost
		);
		console.log(data)
		if (data?.success) {
			setSubmitting(false);
			setSubmitted(true);
		} else {
			setError(error || data?.error || "An error occurred")
			setSubmitting(false);
		}
	}



	function handleWitnessTutorChanged(tutor) {
		setFormData({
			...formData,
			witness_tutor_id: tutor ? tutor.value : null,
		});
	}

	return (
		<div className="incident-report-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title="Incident/Accident Report Form" />
					<NarrowContentWrapper>
						{submitted && (
							<div className="form-submitted mb-3">
								<h3 className="form-submitted__title">
									Thank you
								</h3>
								<div className="form-submitted__message">
									<p>Your Incident Report has been saved.</p>
								</div>
							</div>
						)}
						{loading ? (
							<FieldsLoading />
						) : (
							<>
								{(!submitted) && (
									<>
										<FormBeforeAfterContent content={extraContent?.before ?? null} />
										<form
											action=""
											className="form incident-report-form"
											onSubmit={handleSubmit}
											noValidate>
											<TextInput
												name="name_of_person_in_charge"
												label="Name of person in charge of session"
												value={formData.name_of_person_in_charge}
												error={validationErrors?.name_of_person_in_charge}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="site_of_incident"
												label="Site where incident/accident took place"
												value={formData.site_of_incident}
												error={validationErrors?.site_of_incident}
												onChange={handleFieldChanged}
												required
											/>
											<DatepickerInput
												name="date_of_incident"
												label="Date of Incident/Accident"
												value={formData.date_of_incident}
												error={validationErrors?.date_of_incident}
												onChange={(date) => {
													setFormData({
														...formData,
														date_of_incident: date,
													});
												}}
												required
											/>
											<TextInput
												name="name_of_injured_person"
												label="Name of injured person"
												value={formData.name_of_injured_person}
												error={validationErrors?.name_of_injured_person}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="employer_name"
												label="Employer of Injured Person"
												value={formData.employer_name}
												error={validationErrors?.employer_name}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="nature_of_incident"
												label="Nature of incident/injury and extent of injury"
												value={formData.nature_of_incident}
												error={validationErrors?.nature_of_incident}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="incident_details"
												label="Give details of how and precisely where the incident took place.
												Describe what activity was taking place, for example technique (type)
												or other"
												value={formData.incident_details}
												error={validationErrors?.incident_details}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="details_of_action_taken"
												label="Give full detalls of action taken / first aid treatment / name(s) of first-aider(s)"
												value={formData.details_of_action_taken}
												error={validationErrors?.details_of_action_taken}
												onChange={handleFieldChanged}
												required
											/>
											<label htmlFor="">Were any of the following contacted?</label>
											<div className="incident-report-form__radio-buttons">
												<RadioButtons
													label="Employer"
													name="employer_contacted"
													options={yesNoOptions}
													value={formData.employer_contacted}
													onChange={(val) => {
														handleRadioChanged("employer_contacted", val);
													}}
													required={true}
													error={validationErrors?.employer_contacted}
												/>
												<RadioButtons
													label="Police"
													name="police_contacted"
													options={yesNoOptions}
													value={formData.police_contacted}
													onChange={(val) => {
														handleRadioChanged("police_contacted", val);
													}}
													required={true}
													error={validationErrors?.police_contacted}
												/>
												<RadioButtons
													label="Ambulance"
													name="ambulance_contacted"
													options={yesNoOptions}
													value={formData.ambulance_contacted}
													onChange={(val) => {
														handleRadioChanged("ambulance_contacted", val);
													}}
													required={true}
													error={validationErrors?.ambulance_contacted}
												/>
											</div>
											<TextArea
												name="what_happened_to_injured_person"
												label="What happened to the injured person following the incident / accident? e.g., carried on with session, went home, went to hospital etc."
												value={formData.what_happened_to_injured_person}
												error={validationErrors?.what_happened_to_injured_person}
												onChange={handleFieldChanged}
												required
											/>
											<div className="form__section">
												<h3 className="form__section-title">
													Declarations
												</h3>
												<div className="form__section-body mb-4">
													<p>I declare that the information provided is a true record of the incident / accident.</p>
													<div className="row">
														<div className="col-sm-6">
															<TextInput
																name="witness_student"
																label="Injured Person"
																value={formData.witness_student}
																error={validationErrors?.witness_student}
																onChange={handleFieldChanged}
																required
															/>

														</div>
														<div className="col-sm-6">
															<SelectInput
																name="witness_tutor_id"
																label="Tutor Name"
																containerClasses="mb-2"
																value={
																	formData.witness_tutor_id && tutorOptions.find(
																		(tutor) => tutor.value === formData.witness_tutor_id
																	)
																}
																onChange={handleWitnessTutorChanged}
																options={tutorOptions}
																placeholder="Select Tutor Witness"
															/>
														</div>
													</div>
													{validationErrors?.witness && (
														<div className="invalid-feedback d-block">
															{validationErrors.witness}
														</div>
													)}
												</div>
												<TextArea
													name="other_witness_details"
													label="Please give contact details for any other witnesses"
													value={formData.other_witness_details}
													error={validationErrors?.other_witness_details}
													onChange={handleFieldChanged}
												/>
											</div>
											{error && (
												<AlertMessage type="danger" classes="mb-3" message={error} />
											)}
											{validationErrors && (
												<AlertMessage type="danger" classes="mb-3" message="Please fill in all required fields" />
											)}
											<Button
												text={submitting ? "Saving..." : "Save"}
												type="primary"
												buttonType="submit"
												disabled={isReadOnly}
											/>
										</form>
										<FormBeforeAfterContent content={extraContent?.after ?? null} type="after" />
									</>
								)}
							</>
						)}
					</NarrowContentWrapper>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

export default IncidentReportPage;
